import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueue } from '@/components/QueueContext';
import api from '@/lib/api';
import { giraffeRoot, giraffeSettings, giraffeMemoir, giraffeShareUrl } from '@/lib/giraffePaths';
import { useConfirmation } from '../components/shared/ZCard';
import {
  Settings, ChevronRight,
  MapPin, Hash, Trash2, ExternalLink, Plus, Type, Camera, Quote, Upload, Zap, X,
  DollarSign, Plane, Paperclip, FileText, AlertTriangle, Sparkles, Loader2,
  Mic, Send, Pencil, Layers, ImagePlus, BookOpen, Share2, Copy, Check, Globe, Lock,
  Home
} from 'lucide-react';
import ZTabs from '@/components/shared/ZTabs';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

// Import shared utilities and components
import {
  ICON_MAP, STATUS_CFG, PHASE_COLORS,
  getPhaseForDay, getIcon, formatCurrency, shortDate, getImageUrl, getEdgeColor
} from '@/components/giraffe';
import { TripStats, InlineEdit, InlineSelect, DocsTab } from '@/components/giraffe';

import { AIProposalDialog } from '@/components/giraffe';
import AIActionSheet from '@/components/AIActionSheet';
import OverflowMenu from '@/components/shared/OverflowMenu';
import CompositionTool from '@/components/shared/CompositionTool';

// Colour per derived trip status (planning / active / complete).
const TRIP_STATUS_COLOR = {
  active: 'text-emerald-400',
  planning: 'text-amber-400',
  complete: 'text-slate-400',
};

// Override options for the header control. Empty value = clear override and
// fall back to the date-derived status.
const TRIP_OVERRIDE_OPTIONS = [
  { value: '', label: 'auto (by dates)' },
  { value: 'planning', label: 'planning' },
  { value: 'active', label: 'active' },
  { value: 'complete', label: 'complete' },
];

export default function GiraffeTrip() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { count: queueCount, openQueue } = useQueue();
  const feedback = useConfirmation();
  const [trip, setTrip] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showCosts, setShowCosts] = useState(false);
  const [showLogistics, setShowLogistics] = useState(false);
  const [showFrag, setShowFrag] = useState(false);
  const [editingFrag, setEditingFrag] = useState(null); // fragment id being edited, or null for create
  const [activeTab, setActiveTab] = useState('itinerary');
  const [entryDocs, setEntryDocs] = useState({});
  const [draggingEntryId, setDraggingEntryId] = useState(null);
  const today = new Date().toISOString().split('T')[0];
  const hasSidebar = !window.location.hostname.startsWith('giraffe.');
  const [logForm, setLogForm] = useState({ date: '', entry_type: 'logistics', logistics_type: 'flight', title: '', time: '', location: '', confirmation: '', content: '', note: '', status: 'planning' });
  const [fragForm, setFragForm] = useState({ date: today, entry_type: 'fragment', media_type: 'text', title: '', content: '', location: '', quote_text: '', quote_author: '', images: [], overlay_title: '', overlay_text: '', composed_url: '' });
  const [uploading, setUploading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [smartUploading, setSmartUploading] = useState(false);
  const [smartQueue, setSmartQueue] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [proposalImage, setProposalImage] = useState('');
  const [actionTargets, setActionTargets] = useState({}); // index → entryId for UPDATE actions missing target_id
  const [applyError, setApplyError] = useState(null);
  const [applying, setApplying] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const dispositionRef = useRef(null); // 'accept' | 'reject', set before the proposal dialog closes
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [showAISheet, setShowAISheet] = useState(null);
  const [pastedFile, setPastedFile] = useState(null);
  const [showPastePrompt, setShowPastePrompt] = useState(false);
  const [processingPaste, setProcessingPaste] = useState(false);
  const [quickText, setQuickText] = useState('');
  const [quickAdding, setQuickAdding] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [proposalSourceText, setProposalSourceText] = useState('');
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [barDragging, setBarDragging] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  // Postcards linked to this trip (the living trip record) + the composer state.
  const [tripPostcards, setTripPostcards] = useState([]);
  const [composingPostcard, setComposingPostcard] = useState(null); // 'new' | postcard object | null
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [docBlobUrl, setDocBlobUrl] = useState(null);
  // Quick fragment (camera) state
  const [quickFragOpen, setQuickFragOpen] = useState(false);
  const [quickFragFile, setQuickFragFile] = useState(null);
  const [quickFragPreview, setQuickFragPreview] = useState('');
  const [quickFragCaption, setQuickFragCaption] = useState('');
  const [quickFragDate, setQuickFragDate] = useState(today);
  const [quickFragUploading, setQuickFragUploading] = useState(false);
  const quickInputRef = useRef(null);
  const fileRef = useRef(null);
  const composeFileRef = useRef(null);
  const smartRef = useRef(null);
  const fragPhotoRef = useRef(null);
  const [showShare, setShowShare] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => { loadTrip(true); }, [tripId]);

  useEffect(() => {
    const handler = (e) => { if (e.detail?.app === 'giraffe') loadTrip(); };
    window.addEventListener('universal-input-applied', handler);
    return () => window.removeEventListener('universal-input-applied', handler);
  }, [tripId]);

  // Track keyboard height so popup sits above it
  useEffect(() => {
    if (!quickAddOpen) { setKeyboardOffset(0); return; }
    const update = () => {
      const vv = window.visualViewport;
      if (vv) setKeyboardOffset(Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0)));
    };
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    update();
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [quickAddOpen]);

  const openQuickAdd = () => {
    setQuickAddOpen(true);
    window.dispatchEvent(new CustomEvent('quick-add-focus'));
  };

  const closeQuickAdd = () => {
    setQuickAddOpen(false);
    setQuickText('');
    setBarDragging(false);
    window.dispatchEvent(new CustomEvent('quick-add-blur'));
  };

  // Load entry docs when expanded changes
  useEffect(() => {
    if (expanded && !entryDocs[expanded]) {
      api.get(`/trips/${tripId}/entries/${expanded}/documents`)
        .then(({ data }) => setEntryDocs(prev => ({ ...prev, [expanded]: data || [] })))
        .catch(() => setEntryDocs(prev => ({ ...prev, [expanded]: [] })));
    }
  }, [expanded, tripId]);

  // Expand first entry with docs on this date and scroll to its attachments
  const openDateDocs = (date) => {
    const entry = logistics.find(e => e.date === date && (e.doc_count > 0));
    if (!entry) return;
    setExpanded(entry.id);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="entry-${entry.id}"] [data-testid="attachments-section"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  };

  const loadTrip = async (scrollOnLoad = false) => {
    try {
      const { data } = await api.get(`/trips/${tripId}`);
      setTrip(data);
      api.get(`/giraffe/trips/${tripId}/postcards`)
        .then(r => setTripPostcards(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
      if (scrollOnLoad) {
        const today = new Date().toISOString().split('T')[0];
        const sorted = (data.entries || [])
          .filter(e => e.entry_type === 'logistics' && e.date)
          .sort((a, b) => a.date.localeCompare(b.date));
        const target = sorted.find(e => e.date === today)
          || [...sorted].reverse().find(e => e.date <= today)
          || sorted[0];
        if (target) {
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="entry-${target.id}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 150);
        }
      }
    } catch {
      navigate(giraffeRoot());
    }
  };

  const updateField = async (entryId, field, value) => {
    const snapshot = trip?.entries ? [...trip.entries] : [];
    setTrip(prev => ({
      ...prev,
      entries: (prev.entries || []).map(e => e.id === entryId ? { ...e, [field]: value } : e),
    }));
    try {
      await api.put(`/trips/${tripId}/entries/${entryId}`, { [field]: value });
      feedback.confirm();
    } catch {
      setTrip(prev => ({ ...prev, entries: snapshot }));
      feedback.fail();
    }
  };

  const setTripPublic = async (makePublic) => {
    setSharing(true);
    // Optimistic
    setTrip(prev => ({ ...prev, is_public: makePublic, visibility: makePublic ? 'public' : 'private' }));
    try {
      await api.put(`/trips/${tripId}`, { is_public: makePublic, visibility: makePublic ? 'public' : 'private' });
    } catch {
      setTrip(prev => ({ ...prev, is_public: !makePublic, visibility: !makePublic ? 'public' : 'private' }));
      feedback.fail();
    } finally {
      setSharing(false);
    }
  };

  // Edit a trip-level field (dates, status override). Status itself is derived
  // from the dates server-side, so we merge back the server-computed
  // effective_status / derived_status while preserving loaded entries.
  const updateTripField = async (field, value) => {
    const prevVal = trip?.[field];
    setTrip(prev => ({ ...prev, [field]: value }));
    try {
      const { data } = await api.put(`/trips/${tripId}`, { [field]: value });
      setTrip(prev => ({ ...prev, ...data, entries: prev?.entries }));
      feedback.confirm();
    } catch (e) {
      setTrip(prev => ({ ...prev, [field]: prevVal }));
      feedback.fail();
      const msg = e?.response?.data?.detail;
      if (msg) window.alert(msg);
    }
  };

  const openShare = async () => {
    setShareCopied(false);
    setShowShare(true);
    if (!trip?.is_public) await setTripPublic(true);
  };

  const copyShareLink = async () => {
    if (!trip?.public_slug) return;
    try {
      await navigator.clipboard.writeText(giraffeShareUrl(trip.public_slug));
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      feedback.fail();
    }
  };

  const createLogistics = async () => {
    if (!logForm.date || !logForm.title) return;
    const existingEntries = trip?.entries || [];
    const conflict = existingEntries.find(
      e => e.title?.toLowerCase() === logForm.title?.toLowerCase() && e.date === logForm.date
    );
    if (conflict && !window.confirm(`An entry with title "${logForm.title}" already exists on ${logForm.date}. Create anyway?`)) {
      return;
    }
    try {
      const { data: newEntry } = await api.post(`/trips/${tripId}/entries`, logForm);
      setTrip(prev => ({ ...prev, entries: [...(prev.entries || []), newEntry] }));
      setShowLogistics(false);
      setLogForm({ date: '', entry_type: 'logistics', logistics_type: 'flight', title: '', time: '', location: '', confirmation: '', content: '', note: '', status: 'planning' });
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const getDayNumber = (dateStr) => {
    if (!trip?.start_date || !dateStr) return null;
    const start = new Date(trip.start_date);
    const d = new Date(dateStr);
    return Math.floor((d - start) / 86400000) + 1;
  };

  const createFragment = async () => {
    const form = { ...fragForm };
    if (!form.date) form.date = today;
    if (!form.title) {
      const dayNum = getDayNumber(form.date);
      if (dayNum && dayNum > 0) form.title = `Day ${dayNum}`;
    }
    const existingEntries = trip?.entries || [];
    const conflict = existingEntries.find(
      e => e.title?.toLowerCase() === form.title?.toLowerCase() && e.date === form.date
    );
    if (conflict && !window.confirm(`An entry with title "${form.title}" already exists on ${form.date}. Create anyway?`)) {
      return;
    }
    try {
      const { data: newEntry } = await api.post(`/trips/${tripId}/entries`, form);
      setTrip(prev => ({ ...prev, entries: [...(prev.entries || []), newEntry] }));
      if (form.media_type === 'composed' && newEntry?.id) {
        setComposing(true);
        try {
          await api.post(`/trips/${tripId}/entries/${newEntry.id}/compose`);
          // Reload after compose to get the generated image URL
          loadTrip();
        } catch { feedback.fail(); }
        finally { setComposing(false); }
      }
      setShowFrag(false);
      setFragForm({ date: today, entry_type: 'fragment', media_type: 'text', title: '', content: '', location: '', quote_text: '', quote_author: '', images: [], overlay_title: '', overlay_text: '', composed_url: '' });
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const openEditFrag = (entry) => {
    setEditingFrag(entry.id);
    setFragForm({
      date: entry.date || today,
      entry_type: 'fragment',
      media_type: entry.media_type || (entry.quote_text ? 'quote' : (entry.images?.length ? 'photo' : 'text')),
      title: entry.title || '',
      content: entry.content || '',
      location: entry.location || '',
      quote_text: entry.quote_text || '',
      quote_author: entry.quote_author || '',
      images: entry.images || [],
      overlay_title: entry.overlay_title || '',
      overlay_text: entry.overlay_text || '',
      composed_url: entry.composed_url || '',
    });
    setShowFrag(true);
  };

  const updateFragment = async () => {
    const fragId = editingFrag;
    try {
      await api.put(`/trips/${tripId}/entries/${fragId}`, fragForm);
      setTrip(prev => ({
        ...prev,
        entries: (prev.entries || []).map(e => e.id === fragId ? { ...e, ...fragForm } : e),
      }));
      if (fragForm.media_type === 'composed') {
        setComposing(true);
        try {
          await api.post(`/trips/${tripId}/entries/${fragId}/compose`);
          // Reload after compose to get the generated image URL
          loadTrip();
        } catch { feedback.fail(); }
        finally { setComposing(false); }
      }
      setShowFrag(false);
      setEditingFrag(null);
      setFragForm({ date: today, entry_type: 'fragment', media_type: 'text', title: '', content: '', location: '', quote_text: '', quote_author: '', images: [], overlay_title: '', overlay_text: '', composed_url: '' });
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  const deleteEntry = async (entryId) => {
    const snapshot = trip?.entries ? [...trip.entries] : [];
    setTrip(prev => ({ ...prev, entries: (prev.entries || []).filter(e => e.id !== entryId) }));
    if (expanded === entryId) setExpanded(null);
    try {
      await api.delete(`/trips/${tripId}/entries/${entryId}`);
      feedback.confirm();
    } catch {
      setTrip(prev => ({ ...prev, entries: snapshot }));
      feedback.fail();
    }
  };

  const confirmDeleteEntry = (entry) => {
    setDeleteConfirm({ open: true, id: entry.id, name: entry.title || entry.content?.substring(0, 30) || 'this entry' });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload', formData);
      setFragForm(prev => ({ ...prev, images: [data.url] }));
    } catch { feedback.fail(); }
    finally { setUploading(false); }
  };

  // Quick fragment — photo picker → lightweight preview modal → direct entry
  const handleQuickFragSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setQuickFragFile(file);
    setQuickFragCaption('');
    setQuickFragDate(today);
    const reader = new FileReader();
    reader.onload = (ev) => setQuickFragPreview(ev.target.result);
    reader.readAsDataURL(file);
    setQuickFragOpen(true);
    if (fragPhotoRef.current) fragPhotoRef.current.value = '';
  };

  const handleQuickFragConfirm = async () => {
    if (!quickFragFile) return;
    setQuickFragUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', quickFragFile);
      const { data: uploaded } = await api.post('/upload', formData);
      const payload = {
        entry_type: 'fragment',
        media_type: 'photo',
        images: [uploaded.url],
        title: quickFragCaption.trim() || '',
        date: quickFragDate || today,
        is_public: true,
      };
      const { data: newEntry } = await api.post(`/trips/${tripId}/entries`, payload);
      setTrip(prev => ({ ...prev, entries: [...(prev.entries || []), newEntry] }));
      setQuickFragOpen(false);
      setQuickFragFile(null);
      setQuickFragPreview('');
      feedback.confirm();
    } catch { feedback.fail(); }
    finally { setQuickFragUploading(false); }
  };

  const processSmartUploadFile = async (file) => {
    setSmartUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/trips/${tripId}/smart-upload`, formData);
      if (!data?.proposal) {
        setUploadError('The file was read but produced no proposal. Try again or add it manually.');
        return;
      }
      setApplyError(null);
      setProposal(data.proposal);
      setProposalImage(data.image_url || '');
    } catch (err) {
      setUploadError(err?.response?.data?.detail || 'Could not process that file. Try again or add it manually.');
    } finally {
      setSmartUploading(false);
      if (smartRef.current) smartRef.current.value = '';
    }
  };

  const advanceQueue = () => {
    if (smartQueue.length === 0) return;
    const [next, ...rest] = smartQueue;
    setSmartQueue(rest);
    processSmartUploadFile(next);
  };

  const closeProposal = (disposition) => {
    dispositionRef.current = disposition;
    setProposal(null);
    setProposalImage('');
    setApplyError(null);
    if (disposition === 'reject') {
      setActionTargets({});
      if (proposalSourceText) {
        setQuickText(proposalSourceText);
        setProposalSourceText('');
        setQuickAddOpen(true);
        window.dispatchEvent(new CustomEvent('quick-add-focus'));
      }
    }
    advanceQueue();
  };

  const startSmartUpload = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > 1) setSmartQueue(files.slice(1));
    processSmartUploadFile(files[0]);
  };

  const handleSmartUpload = (e) => startSmartUpload(e.target.files);

  // Quick-add parses free text into an entry proposal. Questions about the
  // trip go to Claude over MCP (Phase 5 retired the in-app Q&A branch).
  const handleQuickAdd = async () => {
    const text = quickText?.trim();
    if (!text || quickAdding) return;
    setQuickAdding(true);
    try {
      const { data } = await api.post(`/trips/${tripId}/input`, { input: text });
      setProposalSourceText(text);
      closeQuickAdd();
      setProposal(data.proposal);
      setProposalImage(data.image_url || '');
    } catch (err) {
      feedback.fail();
    } finally {
      setQuickAdding(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', blob, 'recording.webm');
        setSmartUploading(true);
        try {
          const { data } = await api.post(`/trips/${tripId}/smart-upload`, formData);
          setProposal(data.proposal);
          setProposalImage(data.image_url || '');
        } catch (err) {
          feedback.fail();
        } finally {
          setSmartUploading(false);
        }
      };
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch {
      feedback.fail();
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    setRecording(false);
  };

  const handleAcceptProposal = async () => {
    if (!proposal?.actions || proposal.actions.length === 0) {
      setApplyError('This proposal contains no actions to apply.');
      return;
    }
    // Merge manually-chosen entry IDs into UPDATE actions that had no target_id
    const mergedActions = proposal.actions.map((act, i) =>
      act.action === 'update' && !act.target_id && actionTargets[i]
        ? { ...act, target_id: actionTargets[i] }
        : act
    );
    const unresolved = mergedActions.filter(a => a.action === 'update' && !a.target_id);
    if (unresolved.length) {
      setApplyError(`Choose an entry for ${unresolved.length} highlighted update${unresolved.length > 1 ? 's' : ''} before accepting.`);
      return;
    }
    setApplying(true);
    setApplyError(null);
    try {
      const { data } = await api.post(`/trips/${tripId}/apply-actions`, {
        actions: mergedActions,
        image_url: proposalImage,
      });
      const results = data?.results || [];
      const applied = results.filter(r => r.action === 'updated' || r.action === 'created');
      if (applied.length === 0) {
        const skipped = results.find(r => r.action === 'skipped');
        setApplyError(skipped?.reason || 'No changes were applied. The AI may have matched an entry that needed no changes.');
        setApplying(false);
        return;
      }
      loadTrip();
      setApplying(false);
      closeProposal('accept');
    } catch (err) {
      setApplyError(err?.response?.data?.detail || 'Apply failed. Nothing was changed.');
      setApplying(false);
    }
  };

  const handleRejectProposal = () => closeProposal('reject');

  const updateProposalField = (actionIndex, fieldKey, value) => {
    setProposal(prev => {
      const actions = [...prev.actions];
      actions[actionIndex] = { ...actions[actionIndex], fields: { ...actions[actionIndex].fields, [fieldKey]: value } };
      return { ...prev, actions };
    });
  };

  const handleEntryDocUpload = async (e, entryId) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    let succeeded = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post(`/trips/${tripId}/entries/${entryId}/documents`, formData);
        succeeded++;
      } catch (err) {
        feedback.fail();
      }
    }
    if (succeeded > 0) {
      const { data } = await api.get(`/trips/${tripId}/entries/${entryId}/documents`);
      setEntryDocs(prev => ({ ...prev, [entryId]: data || [] }));
      setTrip(prev => ({
        ...prev,
        entries: prev.entries.map(e =>
          e.id === entryId ? { ...e, doc_count: (e.doc_count || 0) + succeeded } : e
        )
      }));
      feedback.confirm();
    }
  };

  const [docDeleteConfirm, setDocDeleteConfirm] = useState({ open: false, docId: null, entryId: null, name: '' });

  const handleDeleteDoc = async (docId, entryId) => {
    try {
      await api.delete(`/trips/${tripId}/documents/${docId}`);
      setEntryDocs(prev => ({
        ...prev,
        [entryId]: (prev[entryId] || []).filter(d => d.id !== docId)
      }));
      setTrip(prev => ({
        ...prev,
        entries: prev.entries.map(e =>
          e.id === entryId ? { ...e, doc_count: Math.max(0, (e.doc_count || 1) - 1) } : e
        )
      }));
      feedback.confirm();
    } catch { feedback.fail(); }
  };

  // Global paste handler for the page
  useEffect(() => {
    const handlePaste = (e) => {
      // Don't intercept if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            setPastedFile(file);
            setShowPastePrompt(true);
            return;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  const handleSmartUploadPaste = async () => {
    if (!pastedFile) return;
    setShowPastePrompt(false);
    setProcessingPaste(true);
    try {
      const formData = new FormData();
      formData.append('file', pastedFile);
      const { data } = await api.post(`/trips/${tripId}/smart-upload`, formData);
      setProposal(data.proposal);
      setProposalImage(data.image_url || '');
    } catch (err) {
      feedback.fail();
    } finally {
      setProcessingPaste(false);
      setPastedFile(null);
    }
  };

  const handleDirectAttachPaste = async () => {
    if (!pastedFile) return;
    setShowPastePrompt(false);
    setProcessingPaste(true);
    
    try {
      const formData = new FormData();
      formData.append('file', pastedFile);
      const { data } = await api.post('/upload', formData);
      
      // Create a new fragment with the image
      if (pastedFile.type.startsWith('image/')) {
        setFragForm(prev => ({ 
          ...prev, 
          media_type: 'photo',
          images: [data.url],
          date: today 
        }));
        setShowFrag(true);
        feedback.confirm();
      } else {
        feedback.confirm();
      }
    } catch (err) {
      feedback.fail();
    } finally {
      setProcessingPaste(false);
      setPastedFile(null);
    }
  };

  const dismissPastePrompt = () => {
    setShowPastePrompt(false);
    setPastedFile(null);
  };

  // Auto-scroll expanded card into view if not fully visible
  useEffect(() => {
    if (expanded) {
      setTimeout(() => {
        const card = document.querySelector(`[data-testid="entry-${expanded}"]`);
        if (card) {
          const rect = card.getBoundingClientRect();
          const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
          if (!isFullyVisible) {
            card.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 150);
    }
  }, [expanded]);

  if (!trip) return <div style={{ padding: '48px 24px' }}><ZLoadingBar accent="#10B981" loading={true} /></div>;

  const logistics = (trip.entries || [])
    .filter(e => e.entry_type === 'logistics')
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  const fragments = (trip.entries || []).filter(e => e.entry_type === 'fragment' || e.entry_type === 'memoir');
  const filtered = filter === 'all' ? logistics : logistics.filter(e => e.status === filter);

  const stats = {
    confirmed: logistics.filter(e => e.status === 'confirmed').length,
    urgent: logistics.filter(e => e.status === 'urgent').length,
    planning: logistics.filter(e => e.status === 'planning').length,
  };

  const totalCost = logistics.reduce((sum, e) => sum + (e.cost_zar || 0), 0);
  const flightCost = logistics.filter(e => e.logistics_type === 'flight').reduce((sum, e) => sum + (e.cost_zar || 0), 0);
  const accomCost = logistics.filter(e => e.logistics_type === 'hotel').reduce((sum, e) => sum + (e.cost_zar || 0), 0);

  return (
    <div className="max-w-4xl mx-auto relative" data-testid="giraffe-trip-page">
      {/* Global Paste Prompt */}
      {showPastePrompt && pastedFile && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <div className="bg-white/[0.05] border border-white/[0.06] rounded-xl p-3 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-200">
            <div className="flex items-center gap-3 mb-3">
              {pastedFile.type?.startsWith('image/') ? (
                <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#0a0a0f] shrink-0">
                  <img src={URL.createObjectURL(pastedFile)} alt="Preview" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-12 h-12 rounded-lg bg-[#6B35B8]/10 flex items-center justify-center shrink-0">
                  <Paperclip size={20} className="text-[#9B6DD4]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#f0f0f5] font-medium truncate">{pastedFile.name || 'Pasted file'}</p>
                <p className="text-xs text-[#b0b4c0]/60 font-mono">{(pastedFile.size / 1024).toFixed(1)}KB</p>
              </div>
              <button onClick={dismissPastePrompt} className="p-1 text-[#b0b4c0]/50 hover:text-[#b0b4c0] transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSmartUploadPaste}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white text-xs font-medium transition-colors"
              >
                <Sparkles size={14} /> Analyze with AI
              </button>
              <button
                onClick={handleDirectAttachPaste}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-[#f0f0f5] text-xs font-medium transition-colors"
              >
                <Paperclip size={14} /> Just Attach
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload error banner */}
      {uploadError && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <div className="bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded-xl p-3 flex items-start gap-2" data-testid="upload-error">
            <AlertTriangle size={16} className="text-[#E24B4A] mt-0.5 shrink-0" />
            <p className="text-sm text-[#f0f0f5] flex-1">{uploadError}</p>
            <button onClick={() => setUploadError(null)} className="text-[#b0b4c0]/60 hover:text-white"><X size={16} /></button>
          </div>
        </div>
      )}

      {/* Processing indicator */}
      {processingPaste && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <div className="bg-white/[0.05] border border-white/[0.06] rounded-xl p-4 flex items-center justify-center gap-2">
            <Loader2 size={16} className="text-[#9B6DD4] animate-spin" />
            <span className="text-sm text-[#b0b4c0]">Processing file...</span>
          </div>
        </div>
      )}
      
      {/* Sticky header — breadcrumb + section nav, always reachable no matter how far you scroll */}
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: 'rgba(10,10,15,0.92)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Row 1 — breadcrumb + trip actions */}
        <div className="flex items-center justify-between gap-3" style={{ minHeight: 44, padding: '0 12px' }}>
          <div className="flex items-center min-w-0" style={{ gap: 4 }}>
            <Link to={giraffeRoot()} title="All trips" className="flex items-center shrink-0 rounded-md px-1.5 py-1 text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.06] transition-colors">
              <Home size={16} strokeWidth={1.5} />
            </Link>
            <Link to={giraffeRoot()} className="shrink-0 transition-colors" style={{ color: '#6b7084', fontSize: 13, textDecoration: 'none' }}
              onMouseEnter={e => e.currentTarget.style.color = '#b0b4c0'}
              onMouseLeave={e => e.currentTarget.style.color = '#6b7084'}>
              Giraffe
            </Link>
            <ChevronRight size={13} className="shrink-0" style={{ color: '#454860' }} />
            <span className="truncate" style={{ color: '#f0f0f5', fontSize: 13, minWidth: 0 }}>{trip.title}</span>
          </div>
          <div className="flex items-center shrink-0" style={{ gap: 2 }}>
            <button data-testid="trip-share-btn" onClick={openShare} className="p-1.5 text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.06] rounded-md transition-colors" title="Share view-only link">
              <Share2 size={17} strokeWidth={1.5} />
            </button>
            <button data-testid="trip-settings-btn" onClick={() => navigate(giraffeSettings(tripId))} className="p-1.5 text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.06] rounded-md transition-colors" title="Trip settings">
              <Settings size={17} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {/* Row 2 — one-click section nav (scrolls horizontally on small screens) */}
        <div className="flex items-center overflow-x-auto" style={{ gap: 2, padding: '0 10px 6px', scrollbarWidth: 'none' }}>
          {[['Trips', ''], ['Countries', 'countries'], ['Postcards', 'postcards'], ['Memoirs', 'memoirs'], ['Archive', 'archive']].map(([label, tab]) => (
            <Link
              key={label}
              to={tab ? `${giraffeRoot()}?tab=${tab}` : giraffeRoot()}
              className="shrink-0 rounded-full px-2.5 py-1 transition-colors hover:bg-white/[0.06]"
              style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8fa3', textDecoration: 'none', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.color = '#f0f0f5'}
              onMouseLeave={e => e.currentTarget.style.color = '#8a8fa3'}
            >
              {label}
            </Link>
          ))}
        </div>
      </div>

      {/* Tab Row */}
      <div className="px-6 pt-2 pb-0">
        <ZTabs
          tabs={[{ id: 'itinerary', label: 'Itinerary' }, { id: 'docs', label: 'Docs' }]}
          activeTab={activeTab}
          onChange={setActiveTab}
          accent="#10B981"
        />
      </div>

      {/* Content area: trip context (hero, queue, phases) is the first row,
          per Section 10.x — these sit inside the content area, below the tabs. */}
      <div className="px-6 pt-4 pb-6">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          {(() => {
            const eff = trip.effective_status || trip.status || 'planning';
            const overridden = !!(trip.status_override && trip.status_override.trim());
            const day = trip.current_day;
            return (
              <>
                <span className={`text-xs font-mono tracking-wider uppercase ${TRIP_STATUS_COLOR[eff] || 'text-emerald-400'}`}>
                  {eff}
                  {eff === 'active' && day ? ` · day ${day}` : ''}
                </span>
                {/* Override control — 'auto' means derive from dates */}
                <span className="flex items-center gap-1 text-[10px] text-[#b0b4c0]/60 font-mono" title={overridden ? 'Status is manually overridden' : 'Status is derived from the trip dates'}>
                  <Pencil size={10} strokeWidth={1.5} className={overridden ? 'text-amber-400' : ''} />
                  <InlineSelect
                    value={trip.status_override || ''}
                    options={TRIP_OVERRIDE_OPTIONS}
                    onSave={(v) => updateTripField('status_override', v)}
                    className="uppercase tracking-wider"
                  />
                </span>
                {/* Editable date range */}
                <span className="flex items-center gap-1 text-xs text-[#b0b4c0]/70 font-mono">
                  <InlineEdit value={trip.start_date || ''} type="date" onSave={(v) => updateTripField('start_date', v)} className="text-xs w-32" displayClassName="font-mono" placeholder="start date" />
                  <span className="opacity-60">→</span>
                  <InlineEdit value={trip.end_date || ''} type="date" onSave={(v) => updateTripField('end_date', v)} className="text-xs w-32" displayClassName="font-mono" placeholder="end date" />
                </span>
              </>
            );
          })()}
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-[#f0f0f5] font-serif" data-testid="trip-title">{trip.title}</h1>
        {trip.description && <p className="text-[#b0b4c0] font-light mt-2 text-base">{trip.description}</p>}
        {trip.public_slug && (
          <Link
            to={giraffeMemoir(trip.public_slug)}
            data-testid="view-memoir-link"
            className="inline-flex items-center gap-2 mt-3 text-xs font-mono tracking-[0.15em] uppercase text-[#9B6DD4] hover:text-[#B88EE8] transition-colors"
          >
            <BookOpen size={12} strokeWidth={1.5} />
            View Memoir
            {trip.memoir_published && <span className="text-[10px] text-emerald-400">● Published</span>}
          </Link>
        )}

        {/* Stats pills */}
        <div className="flex gap-2 mt-5 flex-wrap" data-testid="trip-stats">
          {[
            { label: 'Confirmed', value: stats.confirmed, color: '#52b788' },
            { label: 'Urgent', value: stats.urgent, color: '#e63946' },
            { label: 'Planning', value: stats.planning, color: '#f4a261' },
            { label: 'Flights', value: formatCurrency(flightCost), color: '#c9a96e' },
            { label: 'Total Confirmed', value: formatCurrency(totalCost), color: '#a8dadc' },
          ].map(s => (
            <div key={s.label} className="bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-2">
              <div className="font-mono font-bold text-sm leading-none" style={{ color: s.color }}>{s.value}</div>
              <div className="text-xs text-[#b0b4c0]/70 tracking-wider uppercase font-mono mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Giraffe queue banner */}
      {queueCount.by_app?.giraffe > 0 && (
        <div style={{
          background: 'rgba(232,197,71,0.06)',
          border: '0.5px solid rgba(232,197,71,0.2)',
          borderRadius: 10,
          padding: '10px 14px',
          margin: '0 24px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#E8C547', flexShrink: 0 }} />
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#b0b4c0', margin: 0, flex: 1 }}>
            {queueCount.by_app.giraffe} item{queueCount.by_app.giraffe !== 1 ? 's' : ''} waiting for review
          </p>
          <button
            onClick={() => openQueue('giraffe')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#E8C547', padding: 0 }}
          >
            Review
          </button>
        </div>
      )}

      {/* Phases */}
      {trip.phases && trip.phases.length > 0 && (
        <div className="px-6 pb-2 flex gap-3 flex-wrap" data-testid="trip-phases">
          {trip.phases.map((phase, i) => (
            <div key={i} className="flex items-center gap-2 text-xs font-mono">
              <div className="w-2 h-2 rounded-full" style={{ background: phase.color || PHASE_COLORS[phase.label] || '#b0b4c0' }} />
              <span className="font-bold" style={{ color: phase.color || PHASE_COLORS[phase.label] || '#b0b4c0' }}>{phase.label}</span>
              <span className="text-[#b0b4c0]/70">Days {phase.days}</span>
            </div>
          ))}
        </div>
      )}

      {/* Date calendar strip removed — dates now shown inline in timeline */}

      {/* Itinerary Tab Content */}
      {activeTab === 'itinerary' && (
        <>
          {/* Filter Bar */}
          <div className="px-6 pt-5 pb-2 flex gap-2 flex-wrap items-center" data-testid="filter-bar">
            {['all', 'confirmed', 'urgent', 'planning'].map(f => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider uppercase transition-all cursor-pointer
                  ${filter === f
                    ? 'bg-[#f0f0f5] text-[#0a0a0f]'
                    : 'bg-transparent border border-white/[0.06] text-[#b0b4c0] hover:border-[#b0b4c0]/30'
                  }`}
              >
                {f}
              </button>
            ))}
            <button
              data-testid="toggle-costs"
              onClick={() => setShowCosts(v => !v)}
              className={`px-3 py-1.5 rounded-full text-xs font-mono tracking-wider uppercase transition-all cursor-pointer ml-auto
                ${showCosts
                  ? 'bg-[#c9a96e] text-[#0a0a0f]'
                  : 'border border-[#c9a96e]/30 text-[#c9a96e]'
                }`}
            >
              <DollarSign size={10} className="inline mr-1" />Costs
            </button>
          </div>

          {/* Cost Breakdown */}
          {showCosts && (
            <div className="mx-6 mt-2 mb-3 bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 font-mono text-xs" data-testid="cost-breakdown">
              <p className="text-[#c9a96e] font-bold tracking-wider uppercase mb-3">Confirmed Spend</p>
              <div className="space-y-1.5">
                {logistics.filter(e => e.cost_zar).map(e => (
                  <div key={e.id} className="flex justify-between">
                    <span className="text-[#b0b4c0]">{e.title}</span>
                    <span className="text-[#c9a96e]">{formatCurrency(e.cost_zar)}</span>
                  </div>
                ))}
                <div className="border-t border-white/[0.06] pt-2 mt-2 flex justify-between font-bold">
                  <span className="text-[#f0f0f5]">Total Flights</span>
                  <span className="text-[#c9a96e]">{formatCurrency(flightCost)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span className="text-[#f0f0f5]">Total Accommodation</span>
                  <span className="text-[#c9a96e]">{formatCurrency(accomCost)}</span>
                </div>
                <div className="border-t border-white/[0.06] pt-2 mt-2 flex justify-between font-bold text-sm">
                  <span className="text-[#f0f0f5]">Total Confirmed</span>
                  <span className="text-[#a8dadc]">{formatCurrency(totalCost)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Quick-Add Trigger Bar — Fixed above nav, aligned with card list, hidden when popup, disambiguation, or answer panel is open */}
          {!quickAddOpen && (
            <div className={`fixed bottom-[72px] md:bottom-8 left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-30 pointer-events-none`}>
              <div className="max-w-4xl mx-auto px-[44px]">
                <div
                  className={`pointer-events-auto w-full flex items-center gap-1 border-2 rounded-xl shadow-xl transition-all backdrop-blur-xl ${smartUploading ? 'z-bar-uploading' : barDragging ? 'border-[#9B6DD4]' : 'border-emerald-500/80'}`}
                  style={{ background: 'rgba(10, 10, 15, 0.80)' }}
                  onDragOver={(e) => { e.preventDefault(); setBarDragging(true); }}
                  onDragLeave={() => setBarDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setBarDragging(false); startSmartUpload(e.dataTransfer.files); }}
                >
                  <button
                    onClick={openQuickAdd}
                    disabled={smartUploading}
                    className="flex-1 flex items-center gap-3 px-4 py-3 md:py-6 text-left hover:opacity-90 active:scale-[0.99] transition-all disabled:cursor-wait"
                    data-testid="quick-add-trigger"
                  >
                    <span className="flex-1 text-sm text-white font-medium">
                      {smartUploading ? 'Analyzing upload…' : barDragging ? 'Drop to analyze…' : 'Add to your trip…'}
                    </span>
                    {smartUploading
                      ? <div className="w-5 h-5 border-2 border-white/30 border-t-white/80 rounded-full animate-spin shrink-0" />
                      : <Mic size={22} className="text-white/70 shrink-0" />
                    }
                    <Upload size={22} className="text-white/70 shrink-0" />
                  </button>
                  <button className="shrink-0 px-3 py-3 md:py-6" title="New postcard" onClick={() => setComposingPostcard('new')} data-testid="compose-postcard-trigger">
                    <ImagePlus size={22} style={{ color: '#10B981' }} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Quick-Add Popup — sits above keyboard, centered 70%, persistent until dismissed */}
          {quickAddOpen && (
            <>
              <div className="fixed inset-0 z-[55]" onClick={closeQuickAdd} />
              <div
                className={`fixed left-0 ${hasSidebar ? 'md:left-64' : ''} right-0 z-[60] pb-2`}
                style={{ bottom: keyboardOffset }}
                onClick={(e) => e.stopPropagation()}
                onDragOver={(e) => { e.preventDefault(); setBarDragging(true); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setBarDragging(false); }}
                onDrop={(e) => { e.preventDefault(); setBarDragging(false); startSmartUpload(e.dataTransfer.files); }}
              >
                <div className="max-w-4xl mx-auto px-[44px]">
                <div
                  className={`w-full border-2 rounded-2xl shadow-2xl overflow-hidden transition-colors backdrop-blur-xl ${
                    smartUploading ? 'z-bar-uploading' : barDragging ? 'border-[#9B6DD4]' : recording ? 'border-red-500/80' : 'border-emerald-500/80'
                  }`}
                  style={{ background: 'rgba(10, 10, 15, 0.92)' }}
                >
                  {/* Title + dismiss */}
                  <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                    <span className="text-[10px] font-mono text-white tracking-widest uppercase flex-1">Add to trip</span>
                    <button onClick={closeQuickAdd} className="p-1 text-white/30 hover:text-white/70 transition-colors">
                      <X size={16} />
                    </button>
                  </div>
                  {/* Input — auto-expands as you type, capped at 30vh then scrolls */}
                  <div className="px-4 pb-3">
                    <textarea
                      ref={quickInputRef}
                      autoFocus
                      value={quickText}
                      rows={1}
                      onChange={(e) => {
                        setQuickText(e.target.value);
                        e.target.style.height = 'auto';
                        // Cap growth at 30vh so the popup never runs off the top of the viewport.
                        const maxPx = Math.round(window.innerHeight * 0.30);
                        e.target.style.height = Math.min(e.target.scrollHeight, maxPx) + 'px';
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickAdd(); } }}
                      onPaste={(e) => {
                        const items = e.clipboardData?.items;
                        if (!items) return;
                        for (let i = 0; i < items.length; i++) {
                          if (items[i].kind === 'file') {
                            const file = items[i].getAsFile();
                            if (file) { e.preventDefault(); processSmartUploadFile(file); return; }
                          }
                        }
                      }}
                      placeholder={smartUploading ? 'Analyzing upload…' : barDragging ? 'Drop to analyze…' : recording ? 'Recording… tap Stop when done' : 'Add anything...'}
                      disabled={recording || quickAdding || smartUploading}
                      className="w-full bg-transparent text-[17px] text-[#f0f0f5] placeholder-white/25 focus:outline-none font-light resize-none"
                      style={{ minHeight: '28px', maxHeight: '30vh', overflowY: 'auto' }}
                      data-testid="quick-add-input"
                    />
                  </div>
                  {/* Action row */}
                  {recording ? (
                    /* Recording: full-width STOP bar */
                    <div className="px-4 py-3 border-t border-white/[0.07]">
                      <button
                        onClick={stopRecording}
                        className="w-full flex items-center justify-center gap-3 py-3 rounded-xl bg-[#E8450A]/15 border border-[#E8450A]/40 transition-all active:scale-[0.98]"
                        data-testid="quick-add-mic"
                      >
                        <div className="flex items-center gap-[3px] h-[18px]">
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '450ms' }} />
                        </div>
                        <span className="text-sm font-mono text-[#E8450A] font-medium tracking-wide">STOP</span>
                        <div className="flex items-center gap-[3px] h-[18px]">
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '80%', animationDelay: '100ms' }} />
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '40%', animationDelay: '250ms' }} />
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '100%', animationDelay: '350ms' }} />
                          <span className="w-[3px] bg-[#E8450A] rounded-full animate-pulse" style={{ height: '60%', animationDelay: '500ms' }} />
                        </div>
                      </button>
                    </div>
                  ) : smartUploading ? (
                    /* Uploading: full-width status bar */
                    <div className="px-4 py-3 border-t border-white/[0.07]">
                      <div className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B6DD4]/10 border border-[#9B6DD4]/20">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white/80 rounded-full animate-spin" />
                        <span className="text-xs font-mono text-[#9B6DD4]/70">Analyzing...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.07]">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={startRecording}
                          className="flex items-center gap-1.5 text-white/55 hover:text-white transition-colors"
                          data-testid="quick-add-mic"
                        >
                          <Mic size={20} />
                          <span className="text-xs font-mono">Voice</span>
                        </button>
                        <label className="flex items-center gap-1.5 text-white/55 hover:text-white transition-colors cursor-pointer" data-testid="quick-add-file">
                          <Upload size={20} />
                          <span className="text-xs font-mono">Attach</span>
                          <input ref={smartRef} type="file" accept="image/*,audio/*,.png,.jpg,.jpeg,.webp,.heic,.mp3,.m4a,.wav,.webm,.ogg,.pdf,.txt" className="hidden" multiple onChange={handleSmartUpload} data-testid="smart-upload-input" />
                        </label>
                        <button type="button" onClick={() => { closeQuickAdd(); setComposingPostcard('new'); }} className="flex items-center gap-1.5 transition-colors" style={{ color: '#10B981' }} title="New postcard" data-testid="quick-add-postcard">
                          <ImagePlus size={20} />
                          <span className="text-xs font-mono">Postcard</span>
                        </button>
                      </div>
                      {quickText.trim() ? (
                        <button onClick={() => handleQuickAdd()} disabled={quickAdding} className="flex items-center gap-2 px-4 py-2 bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full text-sm font-mono transition-colors" data-testid="quick-add-submit">
                          {quickAdding ? <div className="w-4 h-4 border-2 border-emerald-500/40 border-t-emerald-500 rounded-full animate-spin" /> : <><Send size={13} className="shrink-0" /> Send</>}
                        </button>
                      ) : (
                        <button onClick={closeQuickAdd} className="text-xs font-mono text-white/30 hover:text-white/60 px-2 py-1 transition-colors">Cancel</button>
                      )}
                    </div>
                  )}
                </div>
                </div>
              </div>
            </>
          )}

          {/* Timeline — Accordion */}
          <div className="px-6 pb-[200px] md:pb-40" data-testid="trip-timeline">
            {filtered.length === 0 && (
              <p className="text-center text-[#b0b4c0] font-light py-16" data-testid="trip-entries-empty">
                {filter === 'all' ? 'No entries yet.' : `No ${filter} entries.`}
              </p>
            )}

            {filtered.map((entry, i) => {
              const isOpen = expanded === entry.id;
              const cfg = STATUS_CFG[entry.status] || STATUS_CFG.planning;
              const phase = getPhaseForDay(trip.phases, entry.day);
              const phaseColor = phase?.color || PHASE_COLORS[phase?.label] || '#b0b4c0';
              const FlagIcon = getIcon(entry.flag);
              const isKey = entry.flag === 'heart' || entry.title?.includes('Depart') || entry.title?.includes('In Transit') || entry.title?.includes('Quinn & Xavier');
              const docs = entryDocs[entry.id];
              const edgeColor = getEdgeColor(entry);
              const isPast = (entry.end_date || entry.date) < today;
              const isDimmed = !!entry.dimmed;

              const dateObj = entry.date ? new Date(entry.date + 'T12:00:00') : null;
              const isToday = entry.date === today;
              const dateHasDocs = !!entry.date && filtered
                .filter(e => e.date === entry.date)
                .some(e => e.doc_count > 0);

              const prevEntry = i > 0 ? filtered[i - 1] : null;
              const showDateHeader = !prevEntry || prevEntry.date !== entry.date;

              return (
                <div key={entry.id} className="scroll-mt-24" data-testid={`entry-${entry.id}`}>
                  {showDateHeader && (
                    <div
                      data-testid={`date-header-${entry.date || 'none'}`}
                      className={`flex items-center gap-3 ${i === 0 ? 'mt-2' : 'mt-6'} mb-2 px-1`}
                    >
                      {dateObj ? (
                        <>
                          <div
                            onClick={() => dateHasDocs && openDateDocs(entry.date)}
                            className={`flex items-baseline gap-2 ${dateHasDocs ? 'cursor-pointer' : ''}`}
                          >
                            <span className={`text-[10px] font-mono uppercase tracking-[0.2em] ${isToday ? 'text-[#9B6DD4]' : 'text-[#b0b4c0]/70'}`}>
                              {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}
                            </span>
                            <span className={`text-sm font-mono ${isToday ? 'text-[#9B6DD4]' : 'text-[#f0f0f5]'}`}>
                              {dateObj.getDate()} {dateObj.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}
                            </span>
                            {dateHasDocs && <Paperclip size={10} className="text-[#9B6DD4]/70" />}
                          </div>
                          <div className="flex-1 h-px bg-white/[0.06]" />
                        </>
                      ) : (
                        <>
                          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#b0b4c0]/50">No date</span>
                          <div className="flex-1 h-px bg-white/[0.06]" />
                        </>
                      )}
                    </div>
                  )}
                  {/* Card - full width, no left timeline */}
                  <div
                    className={`rounded-xl border border-l-2 transition-all duration-200 mb-2
                      ${isOpen ? 'border-white/[0.06]/60 bg-white/[0.03]' : 'overflow-hidden border-white/[0.06]/60 bg-white/[0.02] hover:bg-white/[0.03]'}
                      ${isKey ? 'ring-1 ring-white/[0.03]' : ''}
                      ${!isPast && isDimmed ? 'opacity-35 hover:opacity-75' : ''}
                    `}
                    style={{ position: 'relative', borderLeftColor: (!isPast && isDimmed) ? '#454860' : '#10B981', opacity: (!isPast && isDimmed) ? undefined : isPast ? 0.45 : 1, transition: 'opacity 0.2s ease' }}
                  >
                    {/* Overflow menu — top-right of card */}
                    <div onClick={e => e.stopPropagation()}>
                      <OverflowMenu items={[
                        { icon: <Sparkles size={16} strokeWidth={1.5} />, label: 'AI Rewrite', onClick: () => setShowAISheet(entry.id) },
                        ...(!isPast ? [{ icon: <span className={`w-2 h-2 rounded-full inline-block ${isDimmed ? 'bg-[#454860]' : 'bg-emerald-400'}`} />, label: isDimmed ? 'Mark Active' : 'Mark Inactive', onClick: () => { const newVal = !entry.dimmed; setTrip(prev => ({ ...prev, entries: prev.entries.map(en => en.id === entry.id ? { ...en, dimmed: newVal } : en) })); updateField(entry.id, 'dimmed', newVal); } }] : []),
                        { icon: <Trash2 size={16} strokeWidth={1.5} />, label: 'Delete', onClick: () => confirmDeleteEntry(entry), destructive: true },
                      ]} />
                    </div>

                    {/* Collapsed header — always visible, sticky when open */}
                    <div
                      data-testid={`toggle-${entry.id}`}
                      onClick={() => setExpanded(isOpen ? null : entry.id)}
                      className={`w-full flex items-center gap-3 px-3 py-3 text-left group cursor-pointer ${
                        isOpen ? 'sticky top-0 z-10 bg-white/[0.03] rounded-t-xl border-b border-white/[0.06]/40' : ''
                      }`}
                    >
                      {/* Status/flag icon (date now lives in the group header) */}
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
                        <FlagIcon size={16} className={cfg.text} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Title row — read-only; title is edited via the Edit with AI overflow */}
                        <div className="flex items-center gap-1.5">
                          <span className="flex-1 min-w-0 text-sm text-[#f0f0f5] font-medium truncate">
                            {entry.title}
                          </span>
                          {entry.status === 'urgent' && (
                            <span className="text-[10px] text-red-400 font-mono animate-pulse shrink-0">BOOK</span>
                          )}
                        </div>

                        {/* Accommodation check-in/out — always visible when hotel */}
                        {entry.logistics_type === 'hotel' && (
                          <div className="flex items-center gap-2 mt-1 text-[11px] font-mono" onClick={e => e.stopPropagation()}>
                            <span style={{ color: cfg.dot }}>
                              IN
                            </span>
                            <InlineEdit
                              value={entry.date}
                              type="date"
                              onSave={(v) => updateField(entry.id, 'date', v)}
                              className="text-[11px] w-28"
                              displayClassName="text-[#f0f0f5]/80 font-medium"
                            />
                            <InlineEdit
                              value={entry.time || ''}
                              onSave={(v) => updateField(entry.id, 'time', v)}
                              placeholder="time"
                              className="text-[11px] w-16"
                              displayClassName="text-[#f0f0f5]/60"
                            />
                            <span style={{ color: '#b0b4c0' }}>·</span>
                            <span style={{ color: cfg.dot }}>
                              OUT
                            </span>
                            <InlineEdit
                              value={entry.end_date || ''}
                              type="date"
                              onSave={(v) => updateField(entry.id, 'end_date', v)}
                              className="text-[11px] w-28"
                              displayClassName="text-[#f0f0f5]/80 font-medium"
                              placeholder="end date"
                            />
                          </div>
                        )}

                        {/* Meta row - date, status, time, cost — all inline-editable, desktop only */}
                        <div className="hidden md:flex items-center gap-1.5 mt-1 text-[11px] font-mono text-[#b0b4c0]/60 flex-wrap" onClick={e => e.stopPropagation()}>
                          <span style={{ color: cfg.dot }}>
                            <InlineEdit
                              value={entry.date}
                              type="date"
                              onSave={(v) => updateField(entry.id, 'date', v)}
                              className="text-[11px] w-28"
                              displayClassName="font-medium"
                            />
                          </span>
                          {(entry.end_date || entry.date) && <span className="flex items-center gap-1">→ <InlineEdit value={entry.end_date || ''} type="date" onSave={(v) => updateField(entry.id, 'end_date', v)} className="text-[11px] w-28" displayClassName="font-medium" placeholder="End date" /></span>}
                          <InlineSelect
                            value={entry.status || 'planning'}
                            options={[
                              { value: 'confirmed', label: 'Confirmed' },
                              { value: 'planning', label: 'Planning' },
                              { value: 'urgent', label: 'Urgent' }
                            ]}
                            onSave={(v) => updateField(entry.id, 'status', v)}
                            className={`px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.text}`}
                          />
                          <InlineEdit
                            value={entry.time || ''}
                            onSave={(v) => updateField(entry.id, 'time', v)}
                            placeholder="Time"
                            className="text-[11px] w-20"
                            displayClassName="text-[#b0b4c0]/70"
                          />
                          <InlineEdit
                            value={entry.cost_zar > 0 ? entry.cost_zar.toString() : ''}
                            type="number"
                            onSave={(v) => updateField(entry.id, 'cost_zar', Number(v))}
                            placeholder="Cost"
                            className="text-[11px] w-20"
                            displayClassName="text-[#c9a96e]"
                          />
                          {entry.cost_zar > 0 && <span className="text-[#b0b4c0]/40">ZAR</span>}
                          {entry.source === 'smart-upload' && (
                            <span className="px-1 py-0.5 rounded bg-[#6B35B8]/10 text-[#9B6DD4]">AI</span>
                          )}
                          {(entry.doc_count > 0) && (
                            <span className="flex items-center gap-0.5 text-[#b0b4c0]/40">
                              <Paperclip size={9} />{entry.doc_count}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Inactive chip — only on collapsed future inactive entries */}
                      {!isPast && isDimmed && !isOpen && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTrip(prev => ({
                              ...prev,
                              entries: prev.entries.map(en =>
                                en.id === entry.id ? { ...en, dimmed: false } : en
                              )
                            }));
                            updateField(entry.id, 'dimmed', false);
                          }}
                          className="text-[10px] font-mono text-[#b0b4c0]/60 hover:text-emerald-400 px-2 py-0.5 rounded border border-white/[0.10] hover:border-emerald-500/40 transition-colors shrink-0"
                        >
                          Inactive
                        </button>
                      )}
                      {/* Chevron */}
                      <ChevronRight
                        size={14}
                        className={`text-[#b0b4c0]/50 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`}
                      />
                    </div>

                    {/* Expanded detail */}
                    {isOpen && (
                      <div className="px-4 pb-4 border-t border-white/[0.06]/50">

                        {/* Sub-items */}
                        {entry.items && entry.items.length > 0 && (
                          <ul className="mt-3 space-y-0">
                            {entry.items.map((item, ii) => {
                              const ItemIcon = getIcon(item.icon);
                              return (
                                <li key={ii} className="flex items-start gap-2.5 py-2 border-b border-dashed border-white/[0.06]/30 last:border-0">
                                  <ItemIcon size={13} className="text-[#b0b4c0]/70 mt-0.5 shrink-0" />
                                  <InlineEdit
                                    value={item.text}
                                    onSave={(v) => {
                                      const newItems = entry.items.map((it, idx) => idx === ii ? { ...it, text: v } : it);
                                      updateField(entry.id, 'items', newItems);
                                    }}
                                    className="text-xs w-full"
                                    displayClassName="text-[#f0f0f5]/80 leading-relaxed"
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {/* Uploaded image */}
                        {entry.images && entry.images.length > 0 && entry.images[0] && (
                          <div className="mt-3">
                            <img src={getImageUrl(entry.images[0])} alt="" className="w-full max-h-[300px] object-cover rounded-lg" />
                          </div>
                        )}

                        {/* Location */}
                        <div className="flex items-center gap-1 mt-3">
                          <MapPin size={10} className="text-[#b0b4c0]/50 shrink-0" />
                          <InlineEdit
                            value={entry.location || ''}
                            onSave={(v) => updateField(entry.id, 'location', v)}
                            placeholder="Add location"
                            className="text-xs w-full"
                            displayClassName="text-[#b0b4c0]/70 font-mono"
                          />
                          {entry.location && <button onClick={() => updateField(entry.id, 'location', '')} className="text-[#b0b4c0]/30 hover:text-red-400 transition-colors shrink-0"><X size={10} /></button>}
                        </div>

                        {/* Confirmation — only shown if populated */}
                        {entry.confirmation && (
                          <div className="flex items-center gap-1 mt-1">
                            <Hash size={10} className="text-[#b0b4c0]/50 shrink-0" />
                            <InlineEdit
                              value={entry.confirmation}
                              onSave={(v) => updateField(entry.id, 'confirmation', v)}
                              className="text-xs w-full"
                              displayClassName="text-[#b0b4c0]/70 font-mono"
                            />
                            <button onClick={() => updateField(entry.id, 'confirmation', '')} className="text-[#b0b4c0]/30 hover:text-red-400 transition-colors shrink-0"><X size={10} /></button>
                          </div>
                        )}

                        {/* Ticket URL — only shown if populated */}
                        {entry.ticket_url && (
                          <div className="flex items-center gap-1 mt-1">
                            <ExternalLink size={10} className="text-[#b0b4c0]/50 shrink-0" />
                            <InlineEdit
                              value={entry.ticket_url}
                              onSave={(v) => updateField(entry.id, 'ticket_url', v)}
                              className="text-xs w-full"
                              displayClassName="text-[#9B6DD4] font-mono"
                            />
                            <a href={entry.ticket_url} target="_blank" rel="noreferrer" className="text-[#9B6DD4] hover:text-[#B88EE8] shrink-0"><ExternalLink size={10} /></a>
                            <button onClick={() => updateField(entry.id, 'ticket_url', '')} className="text-[#b0b4c0]/30 hover:text-red-400 transition-colors shrink-0"><X size={10} /></button>
                          </div>
                        )}

                        {/* Note */}
                        <div className="mt-3 bg-white/[0.05] border border-white/[0.06] rounded-lg px-3 py-2.5">
                          <div className="flex items-start gap-1">
                            <InlineEdit
                              value={entry.note || ''}
                              onSave={(v) => updateField(entry.id, 'note', v)}
                              placeholder="Add a note..."
                              multiline
                              className="text-sm w-full"
                              displayClassName="text-[#c9a96e] italic leading-relaxed"
                            />
                            {entry.note && <button onClick={() => updateField(entry.id, 'note', '')} className="text-[#b0b4c0]/30 hover:text-red-400 transition-colors shrink-0 mt-0.5"><X size={10} /></button>}
                          </div>
                        </div>

                        {/* Content/notes from import */}
                        <div className="flex items-start gap-1 mt-2">
                          <InlineEdit
                            value={entry.content || ''}
                            onSave={(v) => updateField(entry.id, 'content', v)}
                            placeholder="Add notes…"
                            multiline
                            className="text-xs w-full"
                            displayClassName="text-[#b0b4c0] font-light leading-relaxed"
                          />
                          {entry.content && <button onClick={() => updateField(entry.id, 'content', '')} className="text-[#b0b4c0]/30 hover:text-red-400 transition-colors shrink-0 mt-0.5"><X size={10} /></button>}
                        </div>

                        {/* Alert */}
                        <div className="mt-3 bg-red-500/5 border border-red-500/10 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-1">
                            <AlertTriangle size={11} className="text-red-400 shrink-0" />
                            <InlineEdit
                              value={entry.alert || ''}
                              onSave={(v) => updateField(entry.id, 'alert', v)}
                              placeholder="Add alert..."
                              className="text-sm w-full"
                              displayClassName="text-red-400 font-mono"
                            />
                            {entry.alert && <button onClick={() => updateField(entry.id, 'alert', '')} className="text-[#b0b4c0]/30 hover:text-red-400 transition-colors shrink-0"><X size={10} /></button>}
                          </div>
                        </div>

                        {/* Attachments */}
                        <div
                          data-testid="attachments-section"
                          className={`mt-3 pt-2 border-t border-white/[0.06]/30 transition-colors rounded-lg ${
                            draggingEntryId === entry.id ? 'bg-[#6B35B8]/5 border border-dashed border-[#6B35B8]' : ''
                          }`}
                          onDragOver={(e) => { e.preventDefault(); setDraggingEntryId(entry.id); }}
                          onDragLeave={(e) => { e.preventDefault(); if (!e.currentTarget.contains(e.relatedTarget)) setDraggingEntryId(null); }}
                          onDrop={async (e) => {
                            e.preventDefault();
                            setDraggingEntryId(null);
                            const files = Array.from(e.dataTransfer.files || []);
                            if (!files.length) return;
                            let succeeded = 0;
                            for (const file of files) {
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                await api.post(`/trips/${tripId}/entries/${entry.id}/documents`, formData);
                                succeeded++;
                              } catch (err) {
                                feedback.fail();
                              }
                            }
                            if (succeeded > 0) {
                              const { data } = await api.get(`/trips/${tripId}/entries/${entry.id}/documents`);
                              setEntryDocs(prev => ({ ...prev, [entry.id]: data || [] }));
                              setTrip(prev => ({
                                ...prev,
                                entries: prev.entries.map(e =>
                                  e.id === entry.id ? { ...e, doc_count: (e.doc_count || 0) + succeeded } : e
                                )
                              }));
                              feedback.confirm();
                            }
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-[11px] font-mono text-[#b0b4c0]/50 tracking-wider uppercase">
                              {draggingEntryId === entry.id ? 'Drop file here' : 'Attachments'}
                            </span>
                            <label className="cursor-pointer text-[#b0b4c0]/40 hover:text-[#9B6DD4] transition-colors">
                              <Paperclip size={12} />
                              <input type="file" className="hidden" accept="image/*,application/pdf,text/plain"
                                multiple onChange={(e) => handleEntryDocUpload(e, entry.id)} />
                            </label>
                          </div>
                          {draggingEntryId === entry.id ? (
                            <div className="text-center py-4">
                              <Upload size={20} className="mx-auto text-[#9B6DD4]" />
                            </div>
                          ) : docs === undefined ? null : docs.length === 0 ? (
                            <span className="text-[10px] font-mono text-[#b0b4c0]/30">Drop files here or click <Paperclip size={8} className="inline" /></span>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {docs.map(doc => (
                                <div key={doc.id} className="w-20 relative group/doc">
                                  {doc.file_type === 'image' ? (
                                    <img
                                      src={getImageUrl(doc.url)}
                                      alt={doc.filename}
                                      className="w-20 h-16 object-cover rounded-lg cursor-pointer"
                                      onClick={() => setLightboxUrl(getImageUrl(doc.url))}
                                    />
                                  ) : (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDocBlobUrl(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/content`);
                                      }}
                                      className="w-20 h-16 bg-white/[0.05] border border-white/[0.06] rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#6B35B8]/40"
                                    >
                                      {doc.file_type === 'pdf' ? <FileText size={16} className="text-[#9B6DD4]" /> : <FileText size={16} className="text-[#9B6DD4]" />}
                                      <span className="text-[10px] font-mono text-[#b0b4c0]/50">{doc.file_type === 'pdf' ? 'PDF' : 'TXT'}</span>
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setDocDeleteConfirm({ open: true, docId: doc.id, entryId: entry.id, name: doc.filename || 'this file' })}
                                    className="absolute top-1 right-1 opacity-0 group-hover/doc:opacity-100 p-0.5 bg-black/60 rounded text-white/50 hover:text-red-400"
                                  >
                                    <Trash2 size={10} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                      </div>
                    )}
                    {/* Bottom-left accent line — visually closes the card and separates entries */}
                    <div style={{ position: 'absolute', bottom: 0, left: 0, width: '50%', height: '2px', background: `linear-gradient(to right, ${(!isPast && isDimmed) ? '#454860' : '#10B981'}, transparent)`, borderRadius: '0 999px 999px 0', pointerEvents: 'none' }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Postcards — the living trip record */}
          <div className="px-6 pt-2 pb-10" data-testid="trip-postcards">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="w-12 h-px bg-[#10B981]/40 mb-3" />
                <h2 className="text-lg font-serif text-[#f0f0f5] tracking-tight">Postcards</h2>
              </div>
              <button
                onClick={() => setComposingPostcard('new')}
                data-testid="trip-new-postcard"
                className="flex items-center gap-1.5 bg-[#10B981] hover:bg-[#10B981]/90 text-white rounded-full px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
              >
                <Plus size={14} /> New Postcard
              </button>
            </div>
            {tripPostcards.length === 0 ? (
              <p className="text-sm text-[#b0b4c0]/50 font-light">No postcards yet — capture this trip with one.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-[2px]">
                {tripPostcards.map(pc => (
                  <div
                    key={pc.id}
                    onClick={() => setComposingPostcard(pc)}
                    className="relative cursor-pointer bg-white/[0.02] border border-white/[0.06] overflow-hidden group hover:border-[#b0b4c0] transition-all"
                  >
                    <div className="aspect-[4/5]">
                      {pc.composed_image_url ? (
                        <img src={pc.composed_image_url} alt={pc.title || ''} loading="lazy" className="w-full h-full object-contain bg-black/30" />
                      ) : (
                        <div className="w-full h-full bg-[#0a0a0f] flex items-center justify-center"><ImagePlus size={26} className="text-white/[0.06]" /></div>
                      )}
                      {!pc.is_public && (
                        <div className="absolute bottom-2 left-2 bg-[#0a0a0f]/70 backdrop-blur-sm px-2 py-0.5 rounded-full">
                          <span className="text-[10px] font-mono text-[#b0b4c0] tracking-wider uppercase">Hidden</span>
                        </div>
                      )}
                    </div>
                    {pc.title && <div className="p-2"><p className="text-xs text-[#f0f0f5] truncate" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{pc.title}</p></div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Archive — legacy journal/memoir/fragment entries, read-only */}
          {fragments.length > 0 && (
            <div className="pb-16">
              <div className="px-6 mb-8">
                <div className="w-12 h-px bg-white/[0.10] mb-4" />
                <h2 className="text-lg font-serif text-[#b0b4c0]/70 tracking-tight">Archive</h2>
                <p className="text-[11px] font-mono uppercase tracking-wider text-[#6b7084] mt-1">Earlier entries · read-only</p>
              </div>

              <div className="space-y-0" style={{ opacity: 0.85 }}>
                {fragments.map((entry, i) => {
                  const dayNum = getDayNumber(entry.date);
                  const hasImage = entry.images && entry.images.length > 0 && entry.images[0];
                  const isQuote = entry.media_type === 'quote' || entry.quote_text;

                  const isComposed = entry.media_type === 'composed';

                  return (
                    <div key={entry.id} data-testid={`fragment-${entry.id}`} className="group relative">
                      {/* Composed fragment — full bleed composed image, no text overlay */}
                      {isComposed && (entry.composed_image_url || entry.composed_url || entry.images?.[0]) && (
                        <div className="relative">
                          <img
                            src={entry.composed_image_url || entry.composed_url || getImageUrl(entry.images[0])}
                            alt={entry.overlay_title || entry.title || ''}
                            className="w-full max-h-[70vh] object-cover cursor-pointer"
                            onClick={() => setLightboxUrl(entry.composed_image_url || entry.composed_url || getImageUrl(entry.images[0]))}
                          />
                          {/* Fallback CSS overlay if no composed image yet */}
                          {!entry.composed_image_url && !entry.composed_url && (entry.overlay_title || entry.overlay_text) && (
                            <div className="absolute bottom-0 left-0 right-0" style={{ background: 'rgba(10, 10, 15, 0.85)', padding: '16px 24px' }}>
                              {entry.overlay_title && <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 18, color: '#f0f0f5', lineHeight: 1.2, marginBottom: entry.overlay_text ? 8 : 0 }}>{entry.overlay_title}</p>}
                              {entry.overlay_text && <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14, color: '#b0b4c0', lineHeight: 1.5 }}>{entry.overlay_text}</p>}
                            </div>
                          )}
                          {entry.location && (
                            <div className="absolute bottom-0 right-0 px-4 pb-3">
                              <p className="text-xs text-[#b0b4c0]/70 font-mono flex items-center gap-1">
                                <MapPin size={10} />{entry.location}
                              </p>
                            </div>
                          )}
                          
                        </div>
                      )}

                      {/* Photo fragment — full bleed */}
                      {hasImage && !isComposed && (
                        <div className="relative">
                          <img
                            src={getImageUrl(entry.images[0])}
                            alt={entry.content || entry.title || ''}
                            className="w-full max-h-[70vh] object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-transparent to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 px-6 pb-6">
                            {(entry.title || dayNum) && (
                              <p className="text-xs font-mono text-[#9B6DD4]/80 tracking-wider uppercase mb-1.5">
                                {entry.title || (dayNum ? `Day ${dayNum}` : '')}
                              </p>
                            )}
                            {entry.content && (
                              <p className="text-base text-[#f0f0f5] font-light font-serif leading-relaxed max-w-xl">
                                {entry.content}
                              </p>
                            )}
                            {entry.location && (
                              <p className="text-xs text-[#b0b4c0]/70 font-mono mt-2 flex items-center gap-1">
                                <MapPin size={10} />{entry.location}
                              </p>
                            )}
                          </div>
                          
                        </div>
                      )}

                      {/* Quote fragment */}
                      {!hasImage && isQuote && (
                        <div className="px-6 py-12 flex justify-center">
                          <div className="max-w-lg text-center relative">
                            <div className="text-[#6B35B8]/20 text-6xl font-serif leading-none absolute -top-4 -left-4">"</div>
                            <p className="text-xl text-[#f0f0f5] font-serif italic leading-relaxed px-6">
                              {entry.quote_text || entry.content}
                            </p>
                            {entry.quote_author && (
                              <p className="text-sm text-[#b0b4c0] font-mono mt-4">— {entry.quote_author}</p>
                            )}
                            {(entry.title || dayNum) && (
                              <p className="text-xs text-[#b0b4c0]/60 font-mono mt-3">
                                {entry.title || (dayNum ? `Day ${dayNum}` : '')} · {entry.date}
                              </p>
                            )}
                            
                          </div>
                        </div>
                      )}

                      {/* Text fragment */}
                      {!hasImage && !isQuote && (
                        <div className="px-6 py-8 max-w-2xl mx-auto relative">
                          {(entry.title || dayNum) && (
                            <p className="text-xs font-mono text-[#9B6DD4]/60 tracking-wider uppercase mb-2">
                              {entry.title || (dayNum ? `Day ${dayNum}` : '')}
                            </p>
                          )}
                          <p className="text-base text-[#f0f0f5]/80 font-light font-serif leading-[1.8]">
                            {entry.content}
                          </p>
                          {entry.location && (
                            <p className="text-xs text-[#b0b4c0]/70 font-mono mt-3 flex items-center gap-1">
                              <MapPin size={10} />{entry.location}
                            </p>
                          )}
                          <p className="text-xs text-[#b0b4c0]/60 font-mono mt-2">{entry.date}</p>
                          
                        </div>
                      )}

                      {/* Divider between fragments */}
                      {i < fragments.length - 1 && (
                        <div className="flex justify-center py-2">
                          <div className="w-8 h-px bg-white/[0.06]" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* Docs Tab Content */}
      {activeTab === 'docs' && (
        <DocsTab
          tripId={tripId}
          entries={[...logistics, ...fragments]}
          onDocDeleted={(entryId) => setTrip(prev => ({
            ...prev,
            entries: prev.entries.map(e =>
              e.id === entryId ? { ...e, doc_count: Math.max(0, (e.doc_count || 1) - 1) } : e
            )
          }))}
        />
      )}

      {/* AI Proposal Confirmation */}
      <Dialog open={!!proposal} onOpenChange={(open) => {
        if (open) return;
        if (dispositionRef.current) { dispositionRef.current = null; return; }
        closeProposal('reject');
      }}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5] max-w-lg max-h-[80vh] overflow-y-auto" aria-describedby="ai-proposal-desc">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-[#6B35B8]/20 flex items-center justify-center"><Zap size={14} className="text-[#9B6DD4]" /></div>
              AI Proposal
              {smartQueue.length > 0 && <span className="text-xs font-mono text-[#b0b4c0] font-normal">(+{smartQueue.length} queued)</span>}
            </DialogTitle>
          </DialogHeader>

          {proposal && (
            <div className="space-y-4 mt-2">
              <div className="bg-white/[0.05] rounded-lg p-3 border border-white/[0.06]">
                <p id="ai-proposal-desc" className="text-xs text-[#f0f0f5]/80 font-mono whitespace-pre-line leading-relaxed">{proposal.summary}</p>
              </div>

              {(proposal.actions || []).map((act, i) => (
                <div key={i} className={`rounded-xl border p-4 ${act.action === 'update' ? 'border-amber-500/20 bg-amber-500/[0.03]' : 'border-emerald-500/20 bg-emerald-500/[0.03]'}`} data-testid={`proposal-action-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-xs font-mono tracking-wider px-2 py-0.5 rounded-full ${act.action === 'update' ? 'bg-amber-500/10 text-amber-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                      {act.action === 'update' ? 'UPDATE' : 'CREATE'}
                    </span>
                    <span className="text-sm text-[#f0f0f5] font-medium">
                      {act.action === 'update' ? (act.target_title || 'Unknown entry') : act.fields?.title}
                    </span>
                  </div>

                  {/* Entry picker — shown when AI couldn't resolve which entry to update */}
                  {act.action === 'update' && !act.target_id && (
                    <div className="mb-3">
                      <p className="text-xs text-amber-400/80 mb-1.5 font-mono">⚠ Which entry should this update?</p>
                      <Select
                        value={actionTargets[i] || ''}
                        onValueChange={val => setActionTargets(prev => ({ ...prev, [i]: val }))}
                      >
                        <SelectTrigger className="bg-white/[0.05] border-amber-500/30 text-[#f0f0f5] hover:border-amber-500/60">
                          <SelectValue placeholder="— choose entry —" />
                        </SelectTrigger>
                        <SelectContent className="bg-white/[0.05] border-white/[0.06] z-[200]">
                          {logistics.map(e => (
                            <SelectItem key={e.id} value={e.id} className="text-[#f0f0f5] focus:bg-amber-500/10 focus:text-amber-300">
                              {e.date ? `${e.date} · ` : ''}{e.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <p className="text-xs text-[#b0b4c0] mb-3 italic">{act.reason}</p>
                  {(() => {
                    const current = act.action === 'update' ? logistics.find(e => e.id === act.target_id) : null;
                    const SKIP = ['entry_type', 'logistics_type', 'flag', 'items'];
                    const fields = Object.entries(act.fields || {}).filter(([k, v]) =>
                      v !== undefined && v !== null && v !== '' && !SKIP.includes(k) && !(Array.isArray(v) && v.length === 0)
                    );
                    // items handled separately
                    const newItems = (act.fields?.items || []).filter(it => it.text);
                    const oldItems = current ? (current.items || []).filter(it => it.text) : [];
                    return (
                      <div className="space-y-1.5">
                        {fields.map(([key, val]) => {
                          const oldVal = current ? current[key] : undefined;
                          const changed = current && oldVal !== undefined && String(oldVal) !== String(val);
                          return (
                            <div key={key} className="flex gap-2 text-xs items-start">
                              <span className="text-[#b0b4c0]/70 font-mono w-24 shrink-0 pt-0.5">{key}</span>
                              <div className="flex-1 space-y-0.5">
                                {changed && (
                                  <div className="text-[#b0b4c0]/50 line-through text-[10px] font-mono">{String(oldVal)}</div>
                                )}
                                {key === 'status' ? (
                                  <select value={String(val)} onChange={e => updateProposalField(i, key, e.target.value)}
                                    className="w-full bg-[#0a0a0f] border border-white/[0.06] rounded px-2 py-0.5 text-[#f0f0f5]/80 text-xs focus:border-[#6B35B8] focus:outline-none">
                                    <option value="confirmed">confirmed</option>
                                    <option value="planning">planning</option>
                                    <option value="urgent">urgent</option>
                                  </select>
                                ) : key === 'cost_zar' ? (
                                  <input type="number" value={val}
                                    onChange={e => updateProposalField(i, key, e.target.value === '' ? null : Number(e.target.value))}
                                    className="w-full bg-[#0a0a0f] border border-white/[0.06] rounded px-2 py-0.5 text-[#f0f0f5]/80 text-xs focus:border-[#6B35B8] focus:outline-none" />
                                ) : ['note', 'content', 'alert'].includes(key) ? (
                                  <textarea value={String(val)} onChange={e => updateProposalField(i, key, e.target.value)}
                                    rows={2} className="w-full bg-[#0a0a0f] border border-white/[0.06] rounded px-2 py-0.5 text-[#f0f0f5]/80 text-xs focus:border-[#6B35B8] focus:outline-none resize-none" />
                                ) : (
                                  <input value={String(val)} onChange={e => updateProposalField(i, key, e.target.value)}
                                    className="w-full bg-[#0a0a0f] border border-white/[0.06] rounded px-2 py-0.5 text-[#f0f0f5]/80 text-xs focus:border-[#6B35B8] focus:outline-none" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {newItems.length > 0 && (
                          <div className="flex gap-2 text-xs items-start pt-1">
                            <span className="text-[#b0b4c0]/70 font-mono w-24 shrink-0 pt-0.5">items</span>
                            <div className="flex-1 space-y-1">
                              {oldItems.length > 0 && (
                                <div className="space-y-0.5 pb-1 border-b border-white/[0.06]">
                                  {oldItems.map((it, j) => (
                                    <div key={j} className="text-[#b0b4c0]/50 line-through text-[10px] font-mono">{it.text}</div>
                                  ))}
                                </div>
                              )}
                              {newItems.map((it, j) => (
                                <div key={j} className="text-[#f0f0f5]/70 text-[10px] font-mono">{it.text}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}

              {applyError && (
                <div className="bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded-lg px-3 py-2" data-testid="apply-error">
                  <p className="text-xs text-[#E24B4A] font-mono">{applyError}</p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button
                  data-testid="accept-proposal"
                  onClick={handleAcceptProposal}
                  disabled={applying}
                  className="flex-1 bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full"
                >
                  {applying ? 'Applying...' : 'Accept Changes'}
                </Button>
                <Button
                  data-testid="reject-proposal"
                  variant="outline"
                  onClick={handleRejectProposal}
                  className="border-white/[0.06] text-[#b0b4c0] hover:text-[#f0f0f5] rounded-full px-6"
                >
                  Discard
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share view-only link */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5] max-w-md" aria-describedby="share-desc">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-[#10B981]/15 flex items-center justify-center"><Share2 size={14} className="text-emerald-400" /></div>
              Share itinerary
            </DialogTitle>
          </DialogHeader>
          <p id="share-desc" className="sr-only">Share a public, view-only link to this trip itinerary</p>
          <div className="space-y-4 mt-1">
            <div className="flex items-start gap-2 text-xs text-[#b0b4c0] bg-white/[0.04] border border-white/[0.06] rounded-lg p-3">
              <Globe size={14} className="text-emerald-400 mt-0.5 shrink-0" />
              <span>Anyone with this link can view the itinerary. Costs are hidden, and it is read-only. No sign-in needed.</span>
            </div>

            {/* Link + copy */}
            <div className="flex gap-2">
              <input
                readOnly
                value={trip.public_slug ? giraffeShareUrl(trip.public_slug) : ''}
                onFocusCapture={(e) => e.target.select()}
                className="flex-1 min-w-0 bg-[#0a0a0f] border border-white/[0.06] rounded-lg px-3 py-2 text-xs font-mono text-[#f0f0f5]/80 focus:border-[#6B35B8] focus:outline-none"
                data-testid="share-link-input"
              />
              <Button
                onClick={copyShareLink}
                disabled={!trip.public_slug}
                className="bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-lg px-3 shrink-0"
                data-testid="share-copy-btn"
              >
                {shareCopied ? <><Check size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy</>}
              </Button>
            </div>

            {/* Public toggle */}
            <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.06] rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                {trip.is_public ? <Globe size={15} className="text-emerald-400" /> : <Lock size={15} className="text-[#b0b4c0]" />}
                <span className="text-sm text-[#f0f0f5]">{trip.is_public ? 'Link is live' : 'Link is off'}</span>
              </div>
              <button
                onClick={() => setTripPublic(!trip.is_public)}
                disabled={sharing}
                role="switch"
                aria-checked={!!trip.is_public}
                data-testid="share-public-toggle"
                className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${trip.is_public ? 'bg-emerald-500/80' : 'bg-white/[0.12]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${trip.is_public ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {!trip.is_public && (
              <p className="text-xs text-[#b0b4c0]/60">Turn the link on to let people open it. Turning it off makes the link stop working.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Logistics Composer */}
      <Dialog open={showLogistics} onOpenChange={setShowLogistics}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]">
          <DialogHeader><DialogTitle className="font-serif">Add Logistics</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <Select value={logForm.logistics_type} onValueChange={(v) => setLogForm(p => ({ ...p, logistics_type: v }))}>
                <SelectTrigger data-testid="log-type-select" className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white/[0.05] border-white/[0.06]">
                  <SelectItem value="flight" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Flight</SelectItem>
                  <SelectItem value="hotel" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Hotel</SelectItem>
                  <SelectItem value="transport" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Transport</SelectItem>
                  <SelectItem value="activity" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Activity</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="log-date" type="date" value={logForm.date} onChange={(e) => setLogForm(p => ({ ...p, date: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]" />
            </div>
            <Input data-testid="log-title" placeholder="Title (e.g. JNB → NRT)" value={logForm.title} onChange={(e) => setLogForm(p => ({ ...p, title: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="log-time" placeholder="Time (e.g. 14:30)" value={logForm.time} onChange={(e) => setLogForm(p => ({ ...p, time: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
              <Input data-testid="log-confirmation" placeholder="Confirmation #" value={logForm.confirmation} onChange={(e) => setLogForm(p => ({ ...p, confirmation: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input data-testid="log-location" placeholder="Location" value={logForm.location} onChange={(e) => setLogForm(p => ({ ...p, location: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
              <Select value={logForm.status} onValueChange={(v) => setLogForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white/[0.05] border-white/[0.06]">
                  <SelectItem value="planning" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Planning</SelectItem>
                  <SelectItem value="confirmed" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Confirmed</SelectItem>
                  <SelectItem value="urgent" className="text-[#f0f0f5] focus:bg-[#6B35B8]/20 focus:text-[#f0f0f5]">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea data-testid="log-notes" placeholder="Note (optional)" value={logForm.note} onChange={(e) => setLogForm(p => ({ ...p, note: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 min-h-[60px]" />
            <Button data-testid="log-save" onClick={createLogistics} disabled={!logForm.date || !logForm.title} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fragment Composer / Editor */}
      <Dialog open={showFrag} onOpenChange={(v) => { setShowFrag(v); if (!v) { setEditingFrag(null); setFragForm({ date: today, entry_type: 'fragment', media_type: 'text', title: '', content: '', location: '', quote_text: '', quote_author: '', images: [], overlay_title: '', overlay_text: '', composed_url: '' }); } }}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5]">
          <DialogHeader><DialogTitle className="font-serif">{editingFrag ? 'Edit Fragment' : 'Add Fragment'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <Tabs value={fragForm.media_type} onValueChange={(v) => setFragForm(p => ({ ...p, media_type: v }))}>
                <TabsList className="bg-white/[0.05] border border-white/[0.06] w-full">
                  <TabsTrigger value="text" data-testid="frag-type-text" className="data-[state=active]:bg-[#6B35B8] data-[state=active]:text-white text-[#b0b4c0] flex-1"><Type size={14} /></TabsTrigger>
                  <TabsTrigger value="photo" data-testid="frag-type-photo" className="data-[state=active]:bg-[#6B35B8] data-[state=active]:text-white text-[#b0b4c0] flex-1"><Camera size={14} /></TabsTrigger>
                  <TabsTrigger value="quote" data-testid="frag-type-quote" className="data-[state=active]:bg-[#6B35B8] data-[state=active]:text-white text-[#b0b4c0] flex-1"><Quote size={14} /></TabsTrigger>
                  <TabsTrigger value="composed" data-testid="frag-type-composed" className="data-[state=active]:bg-[#10B981] data-[state=active]:text-white text-[#b0b4c0] flex-1"><Layers size={14} /></TabsTrigger>
                </TabsList>
              </Tabs>
              <Input data-testid="frag-date" type="date" value={fragForm.date} onChange={(e) => setFragForm(p => ({ ...p, date: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5]" />
            </div>
            {fragForm.media_type === 'text' && (
              <>
                <Input data-testid="frag-title" placeholder="Title (optional)" value={fragForm.title} onChange={(e) => setFragForm(p => ({ ...p, title: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
                <Textarea data-testid="frag-content" placeholder="What happened, what you felt..." value={fragForm.content} onChange={(e) => setFragForm(p => ({ ...p, content: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 min-h-[120px]" />
                <Input data-testid="frag-location" placeholder="Location" value={fragForm.location} onChange={(e) => setFragForm(p => ({ ...p, location: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
              </>
            )}
            {fragForm.media_type === 'photo' && (
              <>
                <div 
                  onClick={() => fileRef.current?.click()} 
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (!file || !file.type.startsWith('image/')) return;
                    setUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const { data } = await api.post('/upload', formData);
                      setFragForm(prev => ({ ...prev, images: [data.url] }));
                    } catch { feedback.fail(); }
                    finally { setUploading(false); }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDragEnter={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#6B35B8]', 'bg-[#6B35B8]/5'); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-[#6B35B8]', 'bg-[#6B35B8]/5'); }}
                  className="border-2 border-dashed border-white/[0.06] rounded-xl p-6 text-center cursor-pointer hover:border-[#6B35B8]/40 transition-colors" 
                  data-testid="frag-photo-upload">
                  {fragForm.images[0] ? (
                    <div className="relative">
                      <img src={getImageUrl(fragForm.images[0])} alt="" className="max-h-40 mx-auto rounded-lg" />
                      <button onClick={(e) => { e.stopPropagation(); setFragForm(p => ({ ...p, images: [] })); }}
                        className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white/70 hover:text-white">
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <><Upload size={20} className="mx-auto mb-1 text-[#b0b4c0]" /><p className="text-xs text-[#b0b4c0]">{uploading ? 'Uploading...' : 'Drop image or click to upload'}</p></>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                <Input data-testid="frag-photo-title" placeholder="Title (e.g. Day 5 — Niseko powder)" value={fragForm.title} onChange={(e) => setFragForm(p => ({ ...p, title: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
                <Input data-testid="frag-photo-caption" placeholder="Caption (optional)" value={fragForm.content} onChange={(e) => setFragForm(p => ({ ...p, content: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
                <Input data-testid="frag-photo-location" placeholder="Location" value={fragForm.location} onChange={(e) => setFragForm(p => ({ ...p, location: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
              </>
            )}
            {fragForm.media_type === 'quote' && (
              <>
                <Textarea data-testid="frag-quote-text" placeholder="The quote..." value={fragForm.quote_text} onChange={(e) => setFragForm(p => ({ ...p, quote_text: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 min-h-[80px] italic font-serif" />
                <Input data-testid="frag-quote-author" placeholder="Author" value={fragForm.quote_author} onChange={(e) => setFragForm(p => ({ ...p, quote_author: e.target.value }))} className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70" />
              </>
            )}
            {fragForm.media_type === 'composed' && (
              <>
                {/* Photo upload */}
                <div
                  onClick={() => composeFileRef.current?.click()}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (!file || !file.type.startsWith('image/')) return;
                    setUploading(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const { data } = await api.post('/upload', fd);
                      setFragForm(prev => ({ ...prev, images: [data.url] }));
                    } catch { feedback.fail(); }
                    finally { setUploading(false); }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  className="border-2 border-dashed border-white/[0.06] rounded-xl p-4 text-center cursor-pointer hover:border-[#10B981]/40 transition-colors"
                  data-testid="frag-composed-upload"
                >
                  {fragForm.images[0] ? (
                    <div className="relative">
                      <img src={getImageUrl(fragForm.images[0])} alt="" className="max-h-32 mx-auto rounded-lg" />
                      <button onClick={(e) => { e.stopPropagation(); setFragForm(p => ({ ...p, images: [] })); }}
                        className="absolute top-1 right-1 p-1 bg-black/60 rounded-full text-white/70 hover:text-white">
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <><Upload size={18} className="mx-auto mb-1 text-[#b0b4c0]" /><p className="text-xs text-[#b0b4c0]">{uploading ? 'Uploading...' : 'Drop photo or click'}</p></>
                  )}
                </div>
                <input ref={composeFileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setUploading(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const { data } = await api.post('/upload', fd);
                    setFragForm(prev => ({ ...prev, images: [data.url] }));
                  } catch { feedback.fail(); }
                  finally { setUploading(false); e.target.value = ''; }
                }} />

                {/* Text fields */}
                <Input
                  data-testid="frag-overlay-title"
                  placeholder="Title line"
                  value={fragForm.overlay_title}
                  onChange={(e) => setFragForm(p => ({ ...p, overlay_title: e.target.value, title: e.target.value || p.title }))}
                  className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 font-serif"
                />
                <Textarea
                  data-testid="frag-overlay-text"
                  placeholder="Text block"
                  value={fragForm.overlay_text}
                  onChange={(e) => setFragForm(p => ({ ...p, overlay_text: e.target.value, content: e.target.value || p.content }))}
                  className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] placeholder:text-[#b0b4c0]/70 min-h-[60px]"
                />

                {/* Live preview — fixed bottom block (matches CompositionTool layout) */}
                {fragForm.images[0] && (fragForm.overlay_title || fragForm.overlay_text) && (
                  <div className="relative rounded-xl overflow-hidden" style={{ maxHeight: '280px' }}>
                    <img src={getImageUrl(fragForm.images[0])} alt="" className="w-full object-cover" style={{ maxHeight: '280px' }} />
                    <div className="absolute bottom-0 left-0 right-0" style={{ background: 'rgba(10, 10, 15, 0.85)', padding: '16px 20px' }}>
                      {fragForm.overlay_title && (
                        <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 16, color: '#f0f0f5', lineHeight: 1.2, marginBottom: fragForm.overlay_text ? 8 : 0 }}>{fragForm.overlay_title}</p>
                      )}
                      {fragForm.overlay_text && (
                        <p style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 12, color: '#b0b4c0', lineHeight: 1.5 }}>{fragForm.overlay_text}</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            <Button
              data-testid="frag-save"
              onClick={editingFrag ? updateFragment : createFragment}
              disabled={
                composing ||
                (fragForm.media_type === 'text' && !fragForm.content) ||
                (fragForm.media_type === 'photo' && !fragForm.images[0]) ||
                (fragForm.media_type === 'quote' && !fragForm.quote_text) ||
                (fragForm.media_type === 'composed' && (!fragForm.images[0] || (!fragForm.overlay_title && !fragForm.overlay_text)))
              }
              className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full"
            >
              {composing ? <><Loader2 size={14} className="mr-1 animate-spin" /> Composing...</> : editingFrag ? 'Update' : 'Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={() => deleteEntry(deleteConfirm.id)}
        itemName={deleteConfirm.name}
        itemType="entry"
      />
      <DeleteConfirmDialog
        open={docDeleteConfirm.open}
        onClose={() => setDocDeleteConfirm({ open: false, docId: null, entryId: null, name: '' })}
        onConfirm={() => handleDeleteDoc(docDeleteConfirm.docId, docDeleteConfirm.entryId)}
        itemName={docDeleteConfirm.name}
        itemType="document"
      />

      {/* Quick Fragment Modal — photo preview + caption + date → direct entry, no AI */}
      {quickFragOpen && (
        <>
          <div
            className="fixed inset-0 z-[70] bg-black/75"
            onClick={() => { if (!quickFragUploading) { setQuickFragOpen(false); setQuickFragFile(null); setQuickFragPreview(''); } }}
          />
          <div className="fixed inset-0 z-[75] flex items-end justify-center p-4 md:items-center pointer-events-none">
            <div className="pointer-events-auto w-full max-w-md bg-white/[0.05] border border-white/[0.06] rounded-2xl overflow-hidden shadow-2xl">
              {quickFragPreview && (
                <img src={quickFragPreview} alt="" className="w-full max-h-64 object-cover" />
              )}
              <div className="p-4 space-y-3">
                <textarea
                  autoFocus
                  value={quickFragCaption}
                  onChange={(e) => setQuickFragCaption(e.target.value)}
                  placeholder="What happened here..."
                  rows={2}
                  className="w-full bg-[#111] border border-white/[0.06] rounded-xl px-3 py-2 text-[#f0f0f5] text-sm placeholder-[#b0b4c0]/50 focus:outline-none resize-none"
                  style={{ fontFamily: 'inherit' }}
                />
                <input
                  type="date"
                  value={quickFragDate}
                  onChange={(e) => setQuickFragDate(e.target.value)}
                  className="w-full bg-[#111] border border-white/[0.06] rounded-xl px-3 py-2 text-[#f0f0f5] text-sm focus:outline-none"
                />
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => { setQuickFragOpen(false); setQuickFragFile(null); setQuickFragPreview(''); }}
                    disabled={quickFragUploading}
                    className="flex-1 py-2.5 rounded-xl border border-white/[0.06] text-[#b0b4c0] text-sm hover:text-[#f0f0f5] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleQuickFragConfirm}
                    disabled={quickFragUploading}
                    className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white flex items-center justify-center transition-opacity"
                    style={{ background: '#10B981', opacity: quickFragUploading ? 0.7 : 1 }}
                  >
                    {quickFragUploading
                      ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      : 'Add Fragment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showAISheet && (() => {
        const entry = [...logistics, ...fragments].find(e => e.id === showAISheet);
        if (!entry) return null;
        return (
          <AIActionSheet
            entry={entry}
            sourceApp="giraffe"
            tripId={tripId}
            onClose={() => setShowAISheet(null)}
            onAcceptRewrite={async ({ title, body }) => {
              const updates = { title: title || entry.title, notes: body };
              setTrip(prev => ({
                ...prev,
                entries: (prev.entries || []).map(e => e.id === entry.id ? { ...e, ...updates } : e),
              }));
              try {
                await api.put(`/trips/${tripId}/entries/${entry.id}`, updates);
              } catch {
                loadTrip(); // fallback revert via reload
                feedback.fail();
              }
            }}
          />
        );
      })()}

      {/* Lightbox — portalled to body so nothing clips it */}
      {lightboxUrl && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
          data-testid="entry-doc-lightbox"
        >
          <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2" onClick={() => setLightboxUrl(null)}>
            <X size={24} />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}

      {/* PDF viewer */}
      {docBlobUrl && createPortal(
        <div className="fixed inset-0 z-[9999] bg-[#0a0a0f] flex flex-col" data-testid="entry-pdf-viewer">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
            <span className="text-[10px] font-mono text-[#b0b4c0]/50 tracking-widest uppercase">Document</span>
            <button
              className="text-white/50 hover:text-white p-1 transition-colors"
              onClick={() => setDocBlobUrl(null)}
            >
              <X size={18} />
            </button>
          </div>
          <embed src={docBlobUrl} type="application/pdf" className="flex-1 w-full" />
        </div>,
        document.body
      )}

      {composingPostcard && (
        <CompositionTool
          mode="postcard"
          editingId={composingPostcard !== 'new' ? composingPostcard.id : undefined}
          initialData={composingPostcard !== 'new'
            ? { title: composingPostcard.title || '', body: composingPostcard.body || '', field3: composingPostcard.field3 || '', story: composingPostcard.story || '', gallery: composingPostcard.gallery || [], trip_id: composingPostcard.trip_id ?? tripId }
            : { trip_id: tripId }}
          photo={composingPostcard !== 'new' && composingPostcard.source_photo_url ? { url: composingPostcard.source_photo_url } : undefined}
          onClose={() => { setComposingPostcard(null); loadTrip(); }}
          onSave={() => loadTrip()}
        />
      )}
    </div>
  );
}
